import { useState, useEffect } from "react"
import {
	Anchor,
	Button,
	Flex,
	ScrollArea,
	Stack,
	Text,
	UnstyledButton
} from "@mantine/core"
import { AppModal } from "@components/ui/AppModal/AppModal"
import AgencyCard from "../../components/AgencyCard"
import type { AgencyAffiliation as StoreAffiliation } from "@models/agencyAffiliation.model"
import type { AgencyAffiliation as CardAffiliation } from "../AgencyAffiliationsTypes"
import { useSetActiveAgency } from "../../hooks/useAgentService"
import {
	useAEMDictionary,
	AEM_DICTIONARY_COLLECTIONS
} from "@AEM/hooks/useAEMDictionary"
import modalStyles from "./Modal.module.css"
import { useIsMobile } from "@utils/device.utils"
import { MakeActiveAgencyModal } from "./MakeActiveAgencyModal"

export type SearchType = "abta" | "phone"

interface ChangeActiveAgencyModalProps {
	opened: boolean
	onClose: () => void
	currentAgency: StoreAffiliation
	otherAgencies: StoreAffiliation[]
	onSelectAgency?: (agency: StoreAffiliation | null) => void
	onDrawerClose?: () => void
}

/** Maps the store model to the shape AgencyCard / AgencyAddressBlock expects */
const toCardType = (a: StoreAffiliation): CardAffiliation => ({
	agencyId: a.agencyId,
	agencyName: a.agencyName,
	isDefault: a.isDefault,
	isActive: a.active,
	abta: a.abtaNumber,
	phone: a.telephoneNumber,
	address1: a.address?.line1Addr,
	city: a.address?.cityName,
	state: a.address?.stateCode,
	zip: a.address?.postalCode,
	country: a.address?.countryCode
})

export function ChangeActiveAgencyModal({
	opened,
	onClose,
	currentAgency,
	otherAgencies,
	onSelectAgency,
	onDrawerClose
}: ChangeActiveAgencyModalProps) {
	const { activateAgencyAsync, isActivating } = useSetActiveAgency()
	const { dictionary = {} } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const isMobile = useIsMobile()
	const [selectedId, setSelectedId] = useState<number | null>(null)
	const [selectedAgency, setSelectedAgency] = useState<StoreAffiliation | null>(
		null
	)
	const [confirmAgency, setConfirmAgency] = useState<StoreAffiliation | null>(
		null
	)

	const [frozenOtherAgencies, setFrozenOtherAgencies] = useState(otherAgencies)
	const [frozenCurrentAgency, setFrozenCurrentAgency] = useState(currentAgency)
	const [isConfirming, setIsConfirming] = useState(false)

	// Freeze agencies when modal opens to prevent user seeing updates before modal closes
	useEffect(() => {
		if (opened) {
			setFrozenOtherAgencies(otherAgencies)
			setFrozenCurrentAgency(currentAgency)
			setIsConfirming(false)
		}
	}, [opened, otherAgencies, currentAgency])

	const handleSelect = (id: number | null) => {
		const agency =
			id !== null
				? (frozenOtherAgencies.find((a) => a.agencyId === id) ?? null)
				: null
		setSelectedId(id)
		setSelectedAgency(agency)
		onSelectAgency?.(agency)
	}

	const handleClose = () => {
		onSelectAgency?.(null)
		setSelectedId(null)
		setSelectedAgency(null)
		setConfirmAgency(null)
		setIsConfirming(false)
		onClose()
	}

	const handleConfirm = async () => {
		if (!confirmAgency) return
		setIsConfirming(true)
		await activateAgencyAsync(confirmAgency.agencyId, {
			onSuccess: () => {
				handleClose()
				onDrawerClose?.()
				window.location.reload()
			}
		})
	}

	const currentCard = {
		...toCardType(frozenCurrentAgency),
		isDefault: frozenCurrentAgency.isDefault
	}

	return (
		<>
			<AppModal
				transitionProps={{ duration: 0 }}
				opened={opened && confirmAgency === null}
				onClose={handleClose}
				title={dictionary?.maChangeCurrentAgencyHeader ?? ""}
				titleSize="20px"
				hasScrollableBody={false}
				size={726}
				p={isMobile ? 12 : 42}
			>
				<Stack gap={18} align="center" px={isMobile ? 9 : 32} pt={6}>
					<Text
						fz={18}
						pb={20}
						lh={1.8}
						ta="center"
						c="var(--text-color-primary)"
					>
						{dictionary?.maChangeActiveAgencyModelDescription ?? ""}
					</Text>

					<AgencyCard
						agency={currentCard}
						bannerVariant="active"
						bannerLabel={dictionary?.maCurrentActiveAgencyText ?? ""}
						w="100%"
					/>

					<Stack pt={16} gap={8} w="100%">
						<Text fw={600} fz={18} c="var(--text-color-primary)">
							{dictionary?.maChangeActiveAgencyDailogText ?? ""}
						</Text>
						<ScrollArea.Autosize mah={400} type="auto" scrollbarSize={6}>
							<Stack gap={24}>
								{frozenOtherAgencies.map((agency) => (
									<AgencyCard
										key={agency.agencyId}
										agency={{
											...toCardType(agency),
											isDefault: agency.isDefault
										}}
										bannerVariant="neutral"
										bannerLabel={dictionary?.maMakeActiveAgencyText ?? ""}
										isSelected={selectedId === agency.agencyId}
										onBannerAction={() => handleSelect(agency.agencyId)}
										w="100%"
										bannerActionAlign="start"
									/>
								))}
							</Stack>
						</ScrollArea.Autosize>
					</Stack>

					<Flex
						direction={isMobile ? "column" : "row"}
						justify="center"
						align="center"
						gap={isMobile ? 12 : 48}
						pt={20}
						w="100%"
					>
						<Button
							className={modalStyles.confirmButton}
							onClick={() => {
								if (selectedAgency) {
									setConfirmAgency(selectedAgency)
								}
							}}
							disabled={selectedId == null || isActivating}
							w={isMobile ? "100%" : undefined}
							style={{
								order: isMobile ? -1 : 1,
								...(isMobile && { padding: "10px 24px", height: "48px" })
							}}
						>
							{dictionary?.sailingRegionsLabelPlaceholder ?? ""}
						</Button>
						<UnstyledButton
							className={modalStyles.cancelButton}
							onClick={handleClose}
						>
							{dictionary?.cancelButtonLabel ?? ""}
						</UnstyledButton>
					</Flex>

					<Text
						fz={14}
						fw={500}
						ta="center"
						c="var(--text-color-primary)"
						lh={1.8}
						maw={550}
						pt={6}
					>
						{dictionary?.maChangeActivityFooterMsg ?? ""}{" "}
						<Anchor
							href="/profile/agency-affiliations"
							fz={14}
							fw={500}
							td="underline"
							c="var(--text-color-primary)"
						>
							{dictionary?.maManageAgencyAffiliationsHeader ?? ""}
						</Anchor>
					</Text>
				</Stack>
			</AppModal>

			{confirmAgency && (
				<MakeActiveAgencyModal
					opened={true}
					onClose={() => {
						setConfirmAgency(null)
						setIsConfirming(false)
					}}
					variant="active"
					description={dictionary?.maChangeActivityConfirmMsg ?? ""}
					currentAgency={currentCard}
					newAgency={{
						...toCardType(confirmAgency),
						isDefault: confirmAgency.isDefault
					}}
					onConfirm={handleConfirm}
					isActivating={isConfirming}
				/>
			)}
		</>
	)
}
